import React, { useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { login } from '../api';

const AdminLogin: React.FC = () => {
  const [loginData, setLoginData] = useState({
    adminId: '',
    password: '',
  });
  const [errors, setErrors] = useState({
    adminId: '',
    password: '',
    submit: '',
  });
  const [touched, setTouched] = useState({
    adminId: false,
    password: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const validateField = (name: 'adminId' | 'password', value: string) => {
    const trimmedValue = value.trim();

    if (name === 'adminId') {
      if (!trimmedValue) return 'Admin ID is required.';
      if (trimmedValue.length < 3) return 'Admin ID must be at least 3 characters.';
      return '';
    }

    if (!trimmedValue) return 'Password is required.';
    if (trimmedValue.length < 6) return 'Password must be at least 6 characters.';
    return '';
  };

  const validateForm = () => {
    const nextErrors = {
      adminId: validateField('adminId', loginData.adminId),
      password: validateField('password', loginData.password),
      submit: '',
    };
    setErrors(nextErrors);
    return !nextErrors.adminId && !nextErrors.password;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginData((prev) => ({ ...prev, [name]: value }));
    if (name === 'adminId' || name === 'password') {
      setErrors((prev) => ({
        ...prev,
        [name]: touched[name] ? validateField(name, value) : '',
        submit: '',
      }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name !== 'adminId' && name !== 'password') return;

    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setTouched({ adminId: true, password: true });
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const payload = {
        adminId: loginData.adminId.trim(),
        password: loginData.password,
      };
      const res = await login(payload);
      localStorage.setItem('token', res.data.token);
      setLoginData({ adminId: '', password: '' });
      setErrors({ adminId: '', password: '', submit: '' });
      navigate('/dashboard');
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Login failed. Please check your credentials and try again.';
      setErrors((prev) => ({ ...prev, submit: message }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="container py-5" style={{ marginTop: '120px', marginBottom: '60px' }}>
      <div className="row justify-content-center">
        <div className="col-12 col-md-8 col-lg-5">
          <div className="modal-content">
            <div className="modal-body">
              <h2 className="login-modal-title text-center mb-4">Admin Login</h2>
              {errors.submit && (
                <Alert variant="danger" className="mb-3">
                  {errors.submit}
                </Alert>
              )}
              <Form noValidate onSubmit={handleLoginSubmit}>
                <Form.Group className="mb-3" controlId="formAdminId">
                  <Form.Label>Admin ID</Form.Label>
                  <Form.Control
                    type="text"
                    name="adminId"
                    value={loginData.adminId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter Admin ID"
                    autoComplete="username"
                    isInvalid={touched.adminId && Boolean(errors.adminId)}
                    isValid={touched.adminId && !errors.adminId}
                  />
                  <Form.Control.Feedback type="invalid">{errors.adminId}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3" controlId="formPassword">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={loginData.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter Password"
                    autoComplete="current-password"
                    isInvalid={touched.password && Boolean(errors.password)}
                    isValid={touched.password && !errors.password}
                  />
                  <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
                </Form.Group>
                <div className="text-center">
                  <Button variant="primary" type="submit" className="login-submit-btn" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Logging in...
                      </>
                    ) : (
                      'Login'
                    )}
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdminLogin;
